import type React from "react";
import { createContext, useCallback, useContext, useMemo, useReducer } from "react";

type FirstLandModalState = { isOpen: boolean };

type FirstLandModalAction =
    | { type: "OPEN" }
    | { type: "CLOSE" }
    | { type: "SET_OPEN"; payload: boolean }
    | { type: "TOGGLE" };

export type FirstLandModalApi = {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
    setOpen: (value: boolean) => void;
};

const initialState: FirstLandModalState = { isOpen: false };

function reducer(state: FirstLandModalState, action: FirstLandModalAction): FirstLandModalState {
    switch (action.type) {
        case "OPEN":
            return { ...state, isOpen: true };
        case "CLOSE":
            return { ...state, isOpen: false };
        case "SET_OPEN":
            return { ...state, isOpen: action.payload };
        case "TOGGLE":
            return { ...state, isOpen: !state.isOpen };
        default:
            return state;
    }
}

const FirstLandModalContext = createContext<FirstLandModalApi | null>(null);

export const FirstLandModalStateProvider = ({ children }: React.PropsWithChildren) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const open = useCallback(() => dispatch({ type: "OPEN" }), []);
    const close = useCallback(() => dispatch({ type: "CLOSE" }), []);
    const toggle = useCallback(() => dispatch({ type: "TOGGLE" }), []);
    const setOpen = useCallback((value: boolean) => dispatch({ type: "SET_OPEN", payload: value }), []);

    const value = useMemo<FirstLandModalApi>(
        () => ({ isOpen: state.isOpen, open, close, toggle, setOpen }),
        [state.isOpen, open, close, toggle, setOpen],
    );

    return <FirstLandModalContext.Provider value={value}>{children}</FirstLandModalContext.Provider>;
};

export const useFirstLandModal = (): FirstLandModalApi => {
    const ctx = useContext(FirstLandModalContext);
    if (!ctx) {
        throw new Error("useFirstLandModal must be used within FirstLandModalStateProvider");
    }
    return ctx;
};
